import {Injectable} from '@angular/core';
import {ActivatedRoute, Data, NavigationEnd, Router} from '@angular/router';
import {Observable, throwError} from 'rxjs';
import {catchError, filter, first, map, shareReplay, switchMap, tap} from 'rxjs/operators';
import { RouteData } from '../routing/route-data.model';

/**
 * Service for resolving and caching route data. Primarily used in components that exist outside of `router-outlet` and need access to current `Route` id.
 *
 * This service listens for navigation events and extracts the data from the active (deepest) route.
 * It waits until valid data (i.e. non-null data with a defined `id`) is available, then emits that data and caches it using RxJS's `shareReplay`.
 * The cached value is replayed immediately to any new subscribers so that they do not have to wait for a new navigation event.
 *
 * If the expected data is never emitted (i.e. if no route data contains an `id`), the observable chain will throw an error.
 */
@Injectable({
	providedIn: 'root',
})
export class ActivatedRouteService {
	/**
	 * An observable that emits the resolved route data once available.
	 *
	 * The observable pipeline:
	 * 1. Filters router events to only include NavigationEnd events, ensuring that the navigation has fully completed.
	 * 2. Traverses the ActivatedRoute tree to locate the deepest active route.
	 * 3. Switches to the data observable of the active route.
	 * 4. Uses the `first` operator to emit only the first valid data object (i.e. non-null and with a defined `id`).
	 * 5. Caches the resulting emission so that future subscribers receive the last emitted value immediately.
	 */
	public routeData$: Observable<Data> = this.router.events.pipe(
		// Process only NavigationEnd events to ensure route activation is complete.
		filter((event) => event instanceof NavigationEnd),
		 // Traverse the router state tree to get the active (deepest) route.
		map(
			() => {
				let activatedRoute: ActivatedRoute = this.router.routerState.root;

				while (activatedRoute.firstChild) {
					activatedRoute = activatedRoute.firstChild;
				}

				return activatedRoute;
			}
		),
		// Switch to the route's data observable.
		// (If a user navigates away quickly, the previous request is canceled.)
		switchMap((activatedRoute: ActivatedRoute) => activatedRoute.data),
		// Emit only the first data object that is not null and has an `id`.
		// This will throw an error if such data is never emitted.
		first((data: Data) => data !== null && data.id !== undefined),
		// Update the service's routeId property as a side effect.
		tap(
			(data: Data) => {
				this.routeId = data?.id;

				//TODO: rather than exposing just `routeId` - expose full `RouteData`. For now the issue is that not full config from `RouteData` is returned for some reason.
				console.error(<RouteData>data);
			}
		),
		// Cache the emitted value. With a bufferSize of 1, the last emitted value is replayed to new subscribers.
		// With refCount true, the source subscription is managed based on the number of active subscribers.
		shareReplay({bufferSize: 1, refCount: true}),
		catchError(
			(err) => {
				console.error('Error: routeId is missing', err);
				return throwError(() => new Error('Route data did not resolve correctly. routeId is missing.'));
			}
		)
	);

	/**
	 * Stores the current route id extracted from the resolved route data.
	 * This value is updated when routeData$ emits a valid data object.
	 */
	public routeId: number = null;

	// public routeData: RouteData;

	/**
	 * Constructs the ActivatedRouteService and subscribes to routeData$.
	 *
	 * @param router Angular's Router service for listening to navigation events.
	 */
	constructor(
		private router: Router
	) {
		this.routeData$.subscribe(
			{
				error: (err) => {
					// The error has already been logged in catchError; here we simply rethrow.
					throw new Error(err);
				}
			}
		);
	}
}
